
import { Component, Watch } from 'vue-property-decorator';
import VWrapper from '@/components/shared/VWrapper.vue';
import { namespace } from 'vuex-class';
import { Venue } from '@/interfaces/models/Venue';
import { Staff } from '@/interfaces/models/Staff';
import { Tag } from '@/interfaces/models/Tag';
import { Table as TableModel } from '@/interfaces/models/Table';
import Notification from '@/mixins/Notification';
import StackedForm from '@/mixins/StackedForm';
import TableTable from '@/components/table/TableTable.vue';
import VEmptyState from '@/components/shared/VEmptyState.vue';
import MassActiveForm from '@/components/table/MassActiveForm.vue';
import { mixins } from 'vue-class-component';
import VenueApiService from '@/api/http/VenueApiService';
import TableServiceApi from '@/api/http/TableServiceApi';
import { AxiosResponse } from 'axios';
import { toUrl } from '@/util/helper';
import Filter from '@/interfaces/api/Filter';
import { Permission } from '@/enums/Permission';

const venue = namespace('venue');
const table = namespace('table');
const tag = namespace('tag');

@Component({
  components: { VEmptyState, TableTable, VWrapper, MassActiveForm },
})
export default class Table extends mixins(Notification, StackedForm) {
  @venue.State('active') public venue!: Venue;
  @venue.State('checkoutIntegration') public checkoutIntegration!: any;
  @venue.Action('getCheckoutIntegration') public getCheckoutIntegration!: any;

  @table.State('items') public tables!: Table[];
  @table.State((state) => state.pagination.total) public total!: number;
  @table.State('staff') public staff!: Staff[];
  @table.State('filter') public tableFilter!: Filter;
  @table.Action('fetch') public getTables!: any;
  @table.Action('destroy') public destroy!: any;
  @table.Action('setFilter') public setFilter!: (filter: Filter) => void;
  @table.Action('toggleActive') public toggleActiveReq!: any;
  @table.Action('fetchStaff') public getStaff!: any;

  @tag.State('items') public tags!: Tag[];
  @tag.Action('setFilter') public setTagsFilter!: (filter: Filter) => void;
  @tag.Action('fetch') public getTags!: any;

  public show: boolean = false;
  public showMassActivePanel: boolean = false;
  public selectedWaiterId: string | number | null = null;
  public massActionMode: 'tag' | 'range' = 'tag';

  public $refs!: {
    table: InstanceType<typeof TableTable> & { getSelected: () => TableModel[] };
    massActiveForm: InstanceType<typeof MassActiveForm> & {
      getData: () => any;
      validate: () => Promise<boolean | boolean[]>;
    };
  };

  get lightspeedAuthorized() {
    return this.checkoutIntegration && !!this.checkoutIntegration?.lightspeed?.accessToken;
  }

  public created() {
    if (!this.venue) {
      this.$startLoading('venue');
    }
  }

  public mounted() {
    if (!this.venue) {
      this.$startLoading('venue');
    }

    this.fetchData();
  }

  public async sort(sort: { sortBy: string; sortOrder: string }) {
    const filter = { ...this.tableFilter, ...sort };

    this.fetchTables(filter);
  }

  public async fetchData() {
    if (!this.venue?._id) {
      return;
    }

    await this.fetchTables();
    await this.fetchCheckoutIntegration(this.venue);
    await this.fetchTags();
  }

  public async fetchTables(filter?: Filter) {
    if (!filter) {
      this.$startLoading('table');
    }

    this.setFilter(filter || { venue: this.venue._id });
    await this.getTables({ page: 1 });
    this.$stopLoading('table');
  }

  public async fetchTags() {
    if (!this.$can(Permission.TAG_VIEW)) {
      return;
    }

    this.$startLoading('tags');
    this.setTagsFilter({ venue: this.venue._id });
    await this.getTags();
    this.$stopLoading('tags');
  }

  public async fetchCheckoutIntegration(venue: Venue) {
    if (!this.$can(Permission.VENUE_CHECKOUTINTEGRATION_VIEW) || venue.checkoutIntegration !== 'lightspeed_k_series') {
      return;
    }

    await this.getCheckoutIntegration({
      venue: venue._id,
      integrationType: 'lightspeed_k_series',
    });
  }

  public exportQrs() {
    const selected: TableModel[] = this.$refs.table.getSelected() || [];
    if (selected.length === 0) {
      this.notifyInfo('common.selectItems');
      return;
    }

    const api: VenueApiService = new VenueApiService();
    api.exportTableQrs(selected.map((t: TableModel) => t._id)).then((res: AxiosResponse) => {
      toUrl(res.data, 'tables.zip');
    });
  }

  public async openLightspeedWaiterModal() {
    const selected: TableModel[] = this.$refs.table.getSelected();

    if (!selected.length) {
      this.notifyInfo('common.selectItems');
      return;
    }

    if (!this.lightspeedAuthorized) {
      return;
    }

    await this.viewStaff();

    if (!this.staff?.length) {
      return;
    }

    this.setShowModal(true);
  }

  public setShowModal(showModal: boolean = true) {
    this.show = showModal;

    if (!showModal) {
      this.selectedWaiterId = null;
    }
  }

  public openMassActivePanel(mode: 'tag' | 'range' = 'tag') {
    this.massActionMode = mode;
    this.setShowMassActivePanel(true);
  }

  public setShowMassActivePanel(showModal: boolean = true) {
    if (!showModal) {
      this.massActionMode = 'tag';
    }

    this.showMassActivePanel = showModal;
  }

  public async viewStaff() {
    if (!(this.venue && this.venue._id)) {
      return;
    }

    this.$startLoading('staff');
    await this.getStaff(this.venue._id);
    this.$stopLoading('staff');
  }

  public selectStaff(staffId: string | number) {
    this.selectedWaiterId = staffId;
  }

  public async assignLightspeedWaiter() {
    const selected: TableModel[] = this.$refs.table.getSelected() || [];

    if (!selected.length) {
      this.notifyInfo('common.selectItems');
      return;
    }

    if (!this.selectedWaiterId) {
      return;
    }

    const selectedIds: string[] = selected.map((t: TableModel) => t._id);
    const api: TableServiceApi = new TableServiceApi();

    this.$startLoading('assign_waiter');

    await api
      .assignWaiterToTables({ tableIds: selectedIds, lightspeedWaiterId: this.selectedWaiterId }, this.venue._id)
      .then((res: AxiosResponse) => {
        this.notifySuccess('table.notification.assignLightspeedWaiterSuccess');
      })
      .catch((error) => {
        this.notifyError('table.notification.assignLightspeedWaiterError');
      });

    this.$stopLoading('assign_waiter');
    this.setShowModal(false);
  }

  public saveMassActive() {
    this.$refs.massActiveForm.validate().then(async (result: boolean | boolean[]) => {
      if (!this.isValid(result)) {
        this.notifyError('notification.form');
        return;
      }

      const data = this.$refs.massActiveForm.$refs.form.getData();
      const api: TableServiceApi = new TableServiceApi();

      this.$startLoading('table.massActive');
      await api
        .massToggleActive(data, this.venue._id, this.massActionMode)
        .then((res: AxiosResponse) => {
          this.notifySuccess('table.notification.massActiveSuccess');
        })
        .catch((error) => {
          this.notifyError('table.notification.massActiveError');
        })
        .finally(() => {
          this.$stopLoading('table.massActive');
          this.setShowMassActivePanel(false);
          this.getTables({ page: 1 });
        });
    });
  }

  public async toggleActive({ item, value }: { item: TableModel; value: boolean }) {
    this.$startLoading('activeTableCheckbox');
    await this.toggleActiveReq({ ...item, active: !value, id: item._id });
    this.$stopLoading('activeTableCheckbox');
  }

  @Watch('venue')
  public onVenueChange() {
    this.fetchData();
  }

  public deleteTable(table: Table) {
    this.$startLoading('table');
    this.destroy(table);
    this.$stopLoading('table');
  }
}
